import { useCallback, useEffect, useState } from 'react';
import { Calendar, ChevronRight, Clock, Dumbbell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { formatExerciseDisplay } from '../../../utils/exerciseFormatters';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
    HISTORY_PAGE_SIZE,
    formatWorkoutDuration,
    getHistoryPeriodFromTo,
} from './memberHistoryUtils';

const PERIOD_KEYS = ['week', 'month', '3months', 'all'];

function HistoryToolbar({ timeFilter, onFilterChange, historyTotal, historyPage, totalPages, onPrev, onNext, t, isMobile }) {
    return (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50/80 px-2 py-2 dark:border-white/10 dark:bg-gray-900/50">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 dark:border-white/10 dark:bg-gray-800">
                {PERIOD_KEYS.map((key) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onFilterChange(key)}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                            timeFilter === key
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 hover:bg-gray-100 dark:text-white/75 dark:hover:bg-white/10'
                        } ${isMobile ? 'min-h-[36px]' : ''}`}
                    >
                        {t(`gym.membersManager.history.period.${key}`)}
                    </button>
                ))}
            </div>
            <span className="text-xs text-gray-500 dark:text-white/55">
                {t('gym.membersManager.history.workoutsCount', { count: historyTotal })}
            </span>
            {historyTotal > HISTORY_PAGE_SIZE && (
                <div className="ml-auto flex items-center gap-1.5">
                    <span className="hidden text-xs text-gray-500 sm:inline dark:text-white/55">
                        {t('gym.membersManager.history.pageOf', { current: historyPage + 1, total: totalPages })}
                    </span>
                    <button
                        type="button"
                        disabled={historyPage === 0}
                        onClick={onPrev}
                        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 disabled:opacity-40 dark:border-white/10 dark:bg-gray-800 dark:text-white/90"
                    >
                        {t('gym.membersManager.history.prev')}
                    </button>
                    <button
                        type="button"
                        disabled={historyPage >= totalPages - 1}
                        onClick={onNext}
                        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 disabled:opacity-40 dark:border-white/10 dark:bg-gray-800 dark:text-white/90"
                    >
                        {t('gym.membersManager.history.next')}
                    </button>
                </div>
            )}
        </div>
    );
}

export default function MemberHistoryTab({ member }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 1023px)');
    const [history, setHistory] = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [timeFilter, setTimeFilter] = useState('all');
    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            const { from, to } = getHistoryPeriodFromTo(timeFilter);
            const params = {
                user_id: member.id,
                limit: HISTORY_PAGE_SIZE,
                offset: historyPage * HISTORY_PAGE_SIZE,
            };
            if (from) params.from = from;
            if (to) params.to = to;
            const response = await api.get('/allenamenti.php', { params });
            const raw = response.data;
            let list = [];
            let total = 0;
            if (raw && Array.isArray(raw.data) && typeof raw.total === 'number') {
                list = raw.data;
                total = raw.total;
            } else if (Array.isArray(raw)) {
                const memberAllenamenti = raw.filter((a) => a.user_id === member.id);
                list = memberAllenamenti;
                total = memberAllenamenti.length;
            }
            const historyData = list.map((allenamento) => ({
                id: allenamento.id,
                session_date: allenamento.data_allenamento,
                durata_totale: allenamento.durata_totale,
                note: allenamento.note,
                workout_plan_name: allenamento.scheda_nome || 'Scheda',
                scheda_id: allenamento.scheda_id,
            }));
            historyData.sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
            setHistory(historyData);
            setHistoryTotal(total);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Errore caricamento storico:', err);
            setHistory([]);
            setHistoryTotal(0);
        } finally {
            setLoading(false);
        }
    }, [getAuthenticatedApi, member.id, historyPage, timeFilter]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const loadWorkoutDetails = async (allenamentoId) => {
        setLoadingDetails(true);
        try {
            const api = getAuthenticatedApi();
            const response = await api.get(`/serie_completate.php?allenamento_id=${allenamentoId}`);
            const serieCompletate = response.data || [];
            const exercisesMap = {};
            serieCompletate.forEach((serie) => {
                const exId = serie.esercizio_id;
                if (!exercisesMap[exId]) {
                    exercisesMap[exId] = {
                        id: exId,
                        nome: serie.esercizio_nome || 'Esercizio',
                        series: [],
                    };
                }
                exercisesMap[exId].series.push({
                    ripetizioni: serie.ripetizioni,
                    peso: serie.peso,
                    note: serie.note,
                });
            });
            const workout = history.find((h) => h.id === allenamentoId);
            setSelectedWorkout({ ...workout, exercises: Object.values(exercisesMap) });
            setShowDetailsModal(true);
        } catch (err) {
            console.error('Errore caricamento dettagli:', err);
        } finally {
            setLoadingDetails(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="space-y-3 pb-2">
            <HistoryToolbar
                timeFilter={timeFilter}
                onFilterChange={(key) => { setTimeFilter(key); setHistoryPage(0); }}
                historyTotal={historyTotal}
                historyPage={historyPage}
                totalPages={totalPages}
                onPrev={() => setHistoryPage((p) => p - 1)}
                onNext={() => setHistoryPage((p) => p + 1)}
                t={t}
                isMobile={isMobile}
            />

            {history.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/40">
                    <ul className="divide-y divide-gray-100 dark:divide-white/5">
                        {history.map((session) => {
                            const duration = formatWorkoutDuration(session.durata_totale);
                            const dateLabel = new Date(session.session_date).toLocaleDateString('it-IT', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                            });
                            return (
                                <li key={session.id}>
                                    <button
                                        type="button"
                                        onClick={() => loadWorkoutDetails(session.id)}
                                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-indigo-50/60 dark:hover:bg-indigo-950/20 sm:px-4"
                                    >
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                                            <Dumbbell size={16} aria-hidden />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-semibold text-gray-900 dark:text-white">
                                                {session.workout_plan_name}
                                            </span>
                                            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-white/60">
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar size={12} aria-hidden />
                                                    {dateLabel}
                                                </span>
                                                {duration && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Clock size={12} aria-hidden />
                                                        {duration}
                                                    </span>
                                                )}
                                            </span>
                                            {session.note ? (
                                                <span className="mt-1 block truncate text-xs italic text-gray-500 dark:text-white/50">
                                                    {session.note}
                                                </span>
                                            ) : null}
                                        </span>
                                        <span className="hidden shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 sm:inline dark:bg-emerald-900/40 dark:text-emerald-200">
                                            {t('gym.membersManager.history.completed')}
                                        </span>
                                        <ChevronRight size={16} className="shrink-0 text-gray-400 dark:text-white/40" aria-hidden />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-10 text-center dark:border-white/10 dark:bg-gray-900/30">
                    <Calendar size={40} className="mx-auto mb-2 text-gray-300 dark:text-white/30" aria-hidden />
                    <p className="text-sm font-medium text-gray-700 dark:text-white/85">
                        {timeFilter === 'all'
                            ? t('gym.membersManager.history.noWorkouts')
                            : t('gym.membersManager.history.noWorkoutsInPeriod')}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-white/55">
                        {timeFilter === 'all'
                            ? t('gym.membersManager.history.noWorkoutsDescription')
                            : t('gym.membersManager.history.tryOtherPeriod')}
                    </p>
                </div>
            )}

            {showDetailsModal && selectedWorkout && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 backdrop-blur-sm sm:p-4">
                    <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900">
                        <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-white/10">
                            <div className="min-w-0">
                                <h3 className="truncate text-base font-bold text-gray-900 dark:text-white sm:text-lg">
                                    {selectedWorkout.workout_plan_name}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-white/65 sm:text-sm">
                                    {new Date(selectedWorkout.session_date).toLocaleDateString('it-IT', {
                                        weekday: 'long',
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric',
                                    })}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowDetailsModal(false)}
                                className="rounded-lg px-2 py-1 text-xl text-gray-500 hover:bg-gray-100 dark:text-white/80 dark:hover:bg-white/10"
                                aria-label={t('gym.editExtendedProfile.actions.cancel')}
                            >
                                ×
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto p-4">
                            {loadingDetails ? (
                                <div className="flex justify-center py-6">
                                    <LoadingSpinner />
                                </div>
                            ) : selectedWorkout.exercises?.length > 0 ? (
                                <div className="space-y-2">
                                    {selectedWorkout.exercises.map((exercise, idx) => (
                                        <div key={exercise.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 dark:border-white/10 dark:bg-gray-800/60">
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                {idx + 1}. {exercise.nome}
                                            </p>
                                            <div className="mt-1 space-y-0.5">
                                                {exercise.series.map((serie, sIdx) => (
                                                    <div key={sIdx} className="flex justify-between text-xs text-gray-600 dark:text-white/70">
                                                        <span>Serie {sIdx + 1}</span>
                                                        <span className="font-medium text-gray-900 dark:text-white">
                                                            {serie.peso > 0 ? `${serie.peso} kg × ` : ''}
                                                            {formatExerciseDisplay(serie.ripetizioni, exercise)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="py-6 text-center text-sm text-gray-500 dark:text-white/60">
                                    {t('gym.membersManager.history.noExercises')}
                                </p>
                            )}
                        </div>
                        <div className="shrink-0 border-t border-gray-200 p-3 dark:border-white/10">
                            <button
                                type="button"
                                onClick={() => setShowDetailsModal(false)}
                                className="w-full rounded-lg bg-indigo-600 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                            >
                                {t('gym.membersManager.history.closeDetails')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
